import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { buildWhatsappUrl, buildEnquiryMessage } from "../lib/whatsapp";
import { createEnquiry } from "../services/enquiriesApi";

export default function EnquiryModal({ open, onOpenChange, context }) {
  const [form, setForm] = useState({ name: "", phone: "", requirement: "", message: "" });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({ name: "", phone: "", requirement: "", message: "" });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name";
    if (!form.phone.trim()) errs.phone = "Please enter your phone";
    else if (!/^[0-9+\-\s()]{7,}$/.test(form.phone.trim())) errs.phone = "Enter a valid phone number";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const enquiry = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      requirement: form.requirement.trim(),
      message: form.message.trim(),
      project: context?.projectName || form.requirement.trim(),
      source: context?.projectName ? "project_enquiry" : "enquiry_modal",
    };

    setIsSubmitting(true);

    try {
      await createEnquiry(enquiry);

      const msg = buildEnquiryMessage({
        ...enquiry,
        project: context?.projectName,
      });
      const url = buildWhatsappUrl(msg);

      toast.success("Enquiry saved. Redirecting to WhatsApp...");
      setTimeout(() => {
        window.open(url, "_blank", "noopener");
        onOpenChange(false);
      }, 600);
    } catch (error) {
      console.error("Enquiry submission error:", error);
      toast.error(error.message || "The enquiry could not be saved. Please try again.");
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="enquiry-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-6 bg-black/50 backdrop-blur-md animate-in fade-in duration-300"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          data-testid="enquiry-modal-close"
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-black/5 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5 text-[#1A1A1A]" />
        </button>

        <div className="px-8 pt-10 pb-4">
          <p className="text-xs tracking-[0.2em] text-[#7A2E5D] uppercase font-medium mb-3">Enquire Now</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#1A1A1A]">
            Let's build your dream.
          </h2>
          {context?.projectName && (
            <p className="mt-3 text-[#555] text-base">
              About: <span className="text-[#1A1A1A] font-medium">{context.projectName}</span>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="px-8 pb-8 pt-2 space-y-5" data-testid="enquiry-form">
          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-2" htmlFor="enq-name">Name <span className="text-[#E85D8E]">*</span></label>
            <input
              id="enq-name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              data-testid="enquiry-input-name"
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-4 text-base text-[#1A1A1A] focus:outline-none focus:border-[#7A2E5D] focus:ring-2 focus:ring-[#7A2E5D]/10 transition"
              placeholder="Your full name"
            />
            {errors.name && <p className="text-sm text-red-600 mt-1" data-testid="enquiry-error-name">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-2" htmlFor="enq-phone">Phone <span className="text-[#E85D8E]">*</span></label>
            <input
              id="enq-phone"
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              data-testid="enquiry-input-phone"
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-4 text-base text-[#1A1A1A] focus:outline-none focus:border-[#7A2E5D] focus:ring-2 focus:ring-[#7A2E5D]/10 transition"
              placeholder="+91 00000 00000"
            />
            {errors.phone && <p className="text-sm text-red-600 mt-1" data-testid="enquiry-error-phone">{errors.phone}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-2" htmlFor="enq-req">Project / Requirement</label>
            <input
              id="enq-req"
              name="requirement"
              type="text"
              value={form.requirement}
              onChange={handleChange}
              data-testid="enquiry-input-requirement"
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-4 text-base text-[#1A1A1A] focus:outline-none focus:border-[#7A2E5D] focus:ring-2 focus:ring-[#7A2E5D]/10 transition"
              placeholder="2 BHK in Ramapuram"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-2" htmlFor="enq-msg">Message</label>
            <textarea
              id="enq-msg"
              name="message"
              rows={3}
              value={form.message}
              onChange={handleChange}
              data-testid="enquiry-input-message"
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-4 text-base text-[#1A1A1A] focus:outline-none focus:border-[#7A2E5D] focus:ring-2 focus:ring-[#7A2E5D]/10 transition resize-none"
              placeholder="Tell us a little more (optional)"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            data-testid="enquiry-submit-btn"
            className="brand-gradient text-white rounded-full px-8 py-4 w-full font-medium text-base hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Saving enquiry..." : "Send via WhatsApp"}
          </button>
          <p className="text-center text-xs text-[#555]">By submitting, you'll be redirected to WhatsApp with your message pre-filled.</p>
        </form>
      </div>
    </div>
  );
}
